import { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import { KnowledgeBasePage } from './KnowledgeBasePage';
import { WhatsAppConnectionsPage } from './WhatsAppConnectionsPage';
import { MarketplacesPage } from './MarketplacesPage';
import { ErpsPage } from './ErpsPage';
import { SocialConnectionsPage } from './SocialConnectionsPage';
import { UsersPage } from './UsersPage';
import { CreditsHistoryView } from '../components/CreditsHistoryView';
import { McpConnectionsView } from '../components/mcp/McpConnectionsView';
import { useSearchParams } from 'react-router-dom';
import { PlatformIcon } from '../components/shared/PlatformIcon';
import { PhotoOverwriteWarning } from '../components/shared/PhotoOverwriteWarning';
import {
  CutappProductSyncModal,
  DEFAULT_CUTAPP_FIELD_OPTIONS,
  cutappReportSummary,
  runCutappProductImport,
  unwrapCutappConnection,
} from '../components/shared/CutappProductSync';
import type { CutappConnection, CutappFieldOptions, CutappPhotoMode } from '../services/api';
import { useMenuPermissions } from '../contexts/MenuPermissionsContext';
import { useAuth } from '../contexts/AuthContext';

// ─── Types ────────────────────────────────────────────────────────────────────

interface MarketplaceUrl {
  platform: string;
  url: string;
  price?: string;
  listingId?: string;
}

// Vinculo com o Cut App. Diferente dos marketplaces, ele nao vive em
// marketplaceUrls: mora em metadata.cutapp e a chave de casamento e o SKU.
interface CutappLink {
  sku?: string;
  catalogId?: string;
  sourceCatalogName?: string;
  lastSyncAt?: string;
}

interface Product {
  id: string;
  name: string;
  sku?: string;
  category?: string;
  description?: string;
  price?: string;
  url?: string;
  imageUrls: string[];
  marketplaceUrls: MarketplaceUrl[];
  benefit?: string;
  pain?: string;
  dream?: string;
  objection?: string;
  metadata?: { cutapp?: CutappLink | null } | null;
}

/** Vinculo vivo com o Cut App, ou null. O sync do Cut App remove esta chave quando o item sai do catalogo. */
function cutappLinkOf(product: Product): CutappLink | null {
  const link = product?.metadata?.cutapp;
  return link && typeof link === 'object' ? link : null;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function UploadBtn({ label, onFile, loading, small }: { label: string; onFile: (f: File) => void; loading?: boolean; small?: boolean }) {
  const ref = useRef<HTMLInputElement>(null);
  return (
    <>
      <input ref={ref} type="file" accept="image/png,image/jpeg,image/webp,image/jpg" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) { onFile(f); e.target.value = ''; } }} />
      <button
        onClick={() => ref.current?.click()}
        disabled={loading}
        className={`rounded-xl border border-dashed border-slate-300 bg-slate-50 text-slate-500 hover:border-indigo-400 hover:text-indigo-600 disabled:opacity-50 transition-colors flex items-center justify-center gap-1 ${small ? 'h-20 w-20 text-xs flex-col' : 'px-4 py-2 text-sm'}`}
      >
        {loading ? (small ? <span className="text-[10px]">Enviando</span> : 'Enviando...') : (small ? <><span className="text-xl">+</span><span className="text-[10px]">Foto</span></> : label)}
      </button>
    </>
  );
}

// ─── Marketplace URLs editor ──────────────────────────────────────────────────

const MARKETPLACE_OPTIONS = ['Shopee', 'Mercado Livre', 'Amazon', 'Magalu', 'Americanas', 'Shein', 'Site próprio', 'Outro'];

function platformKey(platform: string) {
  return platform.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]/g, '');
}

function MarketplaceUrlsEditor({ value, onChange }: { value: MarketplaceUrl[]; onChange: (v: MarketplaceUrl[]) => void }) {
  const add = () => onChange([...value, { platform: 'Shopee', url: '', price: '', listingId: '' }]);
  const remove = (i: number) => onChange(value.filter((_, idx) => idx !== i));
  const update = (i: number, field: keyof MarketplaceUrl, v: string) =>
    onChange(value.map((item, idx) => idx === i ? { ...item, [field]: v } : item));

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <label className="text-xs font-semibold text-slate-500">Anuncios em marketplaces</label>
        <button onClick={add} className="text-xs text-indigo-600 hover:underline font-semibold">+ Adicionar</button>
      </div>
      {value.length === 0 && (
        <p className="text-xs text-slate-400 italic">Nenhum anuncio vinculado.</p>
      )}
      {value.map((item, i) => (
        <div key={i} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="grid gap-2 md:grid-cols-[150px_1fr_120px_150px_auto]">
            <select
              value={item.platform}
              onChange={e => update(i, 'platform', e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-2 py-2 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-400"
            >
              {MARKETPLACE_OPTIONS.map(o => <option key={o}>{o}</option>)}
            </select>
            <input
              type="url"
              value={item.url}
              onChange={e => update(i, 'url', e.target.value)}
              placeholder="URL do anuncio"
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-400"
            />
            <input value={item.price || ''} onChange={e => update(i, 'price', e.target.value)} placeholder="Preco" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
            <input value={item.listingId || ''} onChange={e => update(i, 'listingId', e.target.value)} placeholder="ID do anuncio" className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-1 focus:ring-indigo-400" />
            <button onClick={() => remove(i)} className="rounded-lg border border-red-200 px-3 py-2 text-xs font-semibold text-red-500 hover:bg-red-50">Remover</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Photo Gallery Editor ─────────────────────────────────────────────────────

function PhotoGalleryEditor({ productId, imageUrls, onUpdated }: {
  productId: string; imageUrls: string[]; onUpdated: (p: Product) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [deletingUrl, setDeletingUrl] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const form = new FormData();
      form.append('photo', file);
      const res = await api.uploadProductPhoto(productId, form);
      if (!res.success) throw new Error();
      toast.success('Foto adicionada!');
      onUpdated(res.product);
    } catch {
      toast.error('Erro ao enviar foto.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (url: string) => {
    setDeletingUrl(url);
    try {
      const res = await api.deleteProductPhoto(productId, url);
      toast.success('Foto removida.');
      onUpdated(res.product);
    } catch {
      toast.error('Erro ao remover foto.');
    } finally {
      setDeletingUrl(null);
    }
  };

  return (
    <>
      <div>
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">
          Fotos do produto ({imageUrls.length})
        </p>
        <div className="flex flex-wrap gap-2 items-start">
          {imageUrls.map((url, i) => (
            <div key={i} className="relative group">
              <button onClick={() => setPreview(url)} className="block">
                <img
                  src={url}
                  alt={`Foto ${i + 1}`}
                  className="h-20 w-20 rounded-xl object-cover border border-slate-200 hover:opacity-90 transition-opacity"
                />
              </button>
              <button
                onClick={() => handleDelete(url)}
                disabled={deletingUrl === url}
                className="absolute -top-1.5 -right-1.5 hidden group-hover:flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white text-[10px] hover:bg-red-600 disabled:opacity-50"
              >
                ✕
              </button>
            </div>
          ))}
          <UploadBtn label="+ Foto" onFile={handleUpload} loading={uploading} small />
        </div>
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setPreview(null)}>
          <div onClick={e => e.stopPropagation()} className="relative">
            <button onClick={() => setPreview(null)} className="absolute -top-8 right-0 text-white/70 hover:text-white text-sm">✕ Fechar</button>
            <img src={preview} alt="" className="max-h-[85vh] max-w-[90vw] rounded-2xl shadow-2xl object-contain" />
          </div>
        </div>
      )}
    </>
  );
}

// ─── Galeria de vídeos do produto (verticais 9:16) ───────────────────────────────
function youtubeId(url: string): string | null {
  const m = String(url || '').match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  return m ? m[1] : null;
}

function ProductVideoGallery({ productId }: { productId: string }) {
  const [videos, setVideos] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    api.listEcommerceProductVideos(productId)
      .then(res => { if (alive) setVideos(res.videos || []); })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [productId]);

  const handleDelete = async (id: string) => {
    setDeletingId(id);
    try { await api.deleteEcommerceProductVideo(id); setVideos(v => v.filter(x => x.id !== id)); toast.success('Vídeo removido.'); }
    catch { toast.error('Erro ao remover vídeo.'); }
    finally { setDeletingId(null); }
  };

  if (loading || !videos.length) return null;

  return (
    <>
      <div className="border-t border-slate-100 pt-3">
        <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-2">Vídeos do produto ({videos.length})</p>
        <div className="flex flex-wrap gap-2 items-start">
          {videos.map(v => {
            const ytId = youtubeId(v.url);
            return (
              <div key={v.id} className="relative group">
                {ytId ? (
                  <a href={v.url} target="_blank" rel="noopener noreferrer" className="block" title="Abrir vídeo (YouTube/Mercado Livre)">
                    <img src={v.thumbnailUrl || `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`} alt="" className="h-32 w-[72px] rounded-xl object-cover border border-slate-200 bg-black hover:opacity-90 transition-opacity" />
                    <span className="absolute inset-0 flex items-center justify-center text-white/90 text-lg drop-shadow">▶</span>
                  </a>
                ) : (
                  <button onClick={() => setPreview(v.url)} className="block">
                    <video src={v.url} muted preload="metadata" className="h-32 w-[72px] rounded-xl object-cover border border-slate-200 bg-black hover:opacity-90 transition-opacity" />
                  </button>
                )}
                <button
                  onClick={() => handleDelete(v.id)}
                  disabled={deletingId === v.id}
                  className="absolute -top-1.5 -right-1.5 hidden group-hover:flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white text-[10px] hover:bg-red-600 disabled:opacity-50"
                >
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setPreview(null)}>
          <div onClick={e => e.stopPropagation()} className="relative">
            <button onClick={() => setPreview(null)} className="absolute -top-8 right-0 text-white/70 hover:text-white text-sm">✕ Fechar</button>
            <video src={preview} controls autoPlay className="max-h-[85vh] max-w-[90vw] rounded-2xl shadow-2xl object-contain bg-black" />
          </div>
        </div>
      )}
    </>
  );
}

// ─── Product Form ─────────────────────────────────────────────────────────────

function ProductForm({ initial, onSave, onCancel }: {
  initial?: Partial<Product>;
  onSave: (p: Product) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({
    name: initial?.name || '',
    sku: initial?.sku || '',
    category: initial?.category || '',
    description: initial?.description || '',
    price: initial?.price || '',
    url: initial?.url || '',
    benefit: initial?.benefit || '',
    pain: initial?.pain || '',
    dream: initial?.dream || '',
    objection: initial?.objection || '',
  });
  const [marketplaceUrls, setMarketplaceUrls] = useState<MarketplaceUrl[]>(initial?.marketplaceUrls || []);
  const [saving, setSaving] = useState(false);
  const [generatingFields, setGeneratingFields] = useState(false);
  // Produto recém criado (para upload de fotos imediato)
  const [createdProduct, setCreatedProduct] = useState<Product | null>(null);

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));


  const handleGenerateFields = async () => {
    if (!form.name.trim() && !form.description.trim()) {
      return toast.error('Informe nome ou descricao do produto.');
    }
    setGeneratingFields(true);
    try {
      const res = await api.generateProductAiFields({ ...form, marketplaceUrls });
      const fields = res.fields || {};
      setForm(f => ({
        ...f,
        benefit: fields.benefit || f.benefit,
        pain: fields.pain || f.pain,
        dream: fields.dream || f.dream,
        objection: fields.objection || f.objection,
      }));
      toast.success('Campos gerados pela IA.');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao gerar campos.');
    } finally {
      setGeneratingFields(false);
    }
  };
  const handleSubmit = async () => {
    if (!form.name.trim()) return toast.error('Nome é obrigatório.');
    setSaving(true);
    try {
      const payload = { ...form, marketplaceUrls };
      let res;
      if (initial?.id) {
        res = await api.updateProduct(initial.id, payload);
      } else {
        res = await api.createProduct(payload);
      }
      if (!res.success) throw new Error('Erro ao salvar produto.');
      toast.success(initial?.id ? 'Produto atualizado!' : 'Produto criado!');
      if (!initial?.id) {
        // Mostra seção de fotos antes de fechar o form
        setCreatedProduct(res.product);
      } else {
        onSave(res.product);
      }
    } catch (err: any) {
      toast.error(err.message || 'Erro ao salvar produto.');
    } finally {
      setSaving(false);
    }
  };

  const field = (label: string, key: string, placeholder?: string, textarea?: boolean) => (
    <div>
      <label className="block text-xs font-semibold text-slate-500 mb-1">{label}</label>
      {textarea ? (
        <textarea
          value={(form as any)[key]}
          onChange={e => set(key, e.target.value)}
          placeholder={placeholder}
          rows={3}
          className="w-full resize-none rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-400"
        />
      ) : (
        <input
          type="text"
          value={(form as any)[key]}
          onChange={e => set(key, e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-indigo-400"
        />
      )}
    </div>
  );

  return (
    <div className="space-y-4 rounded-2xl border border-indigo-100 bg-white p-5 shadow-sm">
      <h3 className="font-bold text-slate-800">{initial?.id ? 'Editar produto' : 'Novo produto'}</h3>

      <div className="grid gap-3 sm:grid-cols-2">
        {field('Nome *', 'name', 'Ex: Camiseta Premium')}
        {field('SKU', 'sku', 'Ex: CAM-001')}
        {field('Categoria', 'category', 'Ex: Vestuário')}
        {field('Descrição e medidas', 'description', 'Ex: garrafa térmica inox 500ml, 22cm de altura, 7cm de diâmetro, cabe em uma mão...', true)}
        {field('Preço', 'price', 'Ex: R$ 129,90')}
        {field('Site do produto', 'url', 'https://...')}
      </div>

      <MarketplaceUrlsEditor value={marketplaceUrls} onChange={setMarketplaceUrls} />

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <label className="text-xs font-semibold text-slate-500">Campos comerciais</label>
          <button
            type="button"
            onClick={handleGenerateFields}
            disabled={generatingFields || saving}
            className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-xs font-semibold text-indigo-700 hover:bg-indigo-100 disabled:opacity-50"
          >
            {generatingFields ? 'Gerando...' : 'Gerar com IA'}
          </button>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          {field('Benefício principal', 'benefit', 'O que o produto entrega...', true)}
          {field('Dor que resolve', 'pain', 'Problema que elimina...', true)}
          {field('Sonho/resultado', 'dream', 'O que o cliente conquista...', true)}
          {field('Objeção comum', 'objection', 'O que o cliente diz para não comprar...', true)}
        </div>
      </div>

      {createdProduct && (
        <div className="border-t border-slate-100 pt-4">
          <PhotoGalleryEditor
            productId={createdProduct.id}
            imageUrls={createdProduct.imageUrls}
            onUpdated={p => setCreatedProduct(p)}
          />
        </div>
      )}

      <div className="flex justify-end gap-3 pt-1">
        <button onClick={onCancel} className="rounded-xl border border-slate-200 px-4 py-2 text-sm text-slate-600 hover:bg-slate-50">
          Cancelar
        </button>
        {createdProduct ? (
          <button
            onClick={() => onSave(createdProduct)}
            className="rounded-xl bg-emerald-600 px-5 py-2 text-sm font-semibold text-white hover:bg-emerald-700"
          >
            ✓ Concluir
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="rounded-xl bg-indigo-600 px-5 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? 'Salvando...' : initial?.id ? 'Salvar alterações' : 'Criar produto ->'}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Product Card ─────────────────────────────────────────────────────────────

function ProductCard({ product, onUpdated, onDeleted, selected, onToggleSelect }: {
  product: Product;
  onUpdated: (p: Product) => void;
  onDeleted: (id: string) => void;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
}) {
  const [editing, setEditing] = useState(false);

  const handleDelete = async () => {
    if (!confirm(`Apagar produto "${product.name}"?\n\nTodos os registros de métricas vinculados a este anúncio também serão apagados. Essa ação não pode ser desfeita.`)) return;
    try {
      await api.deleteProduct(product.id);
      toast.success('Produto apagado.');
      onDeleted(product.id);
    } catch (err: any) {
      toast.error(err.message || 'Erro ao apagar produto.');
    }
  };

  if (editing) {
    return (
      <ProductForm
        initial={product}
        onSave={p => { onUpdated(p); setEditing(false); }}
        onCancel={() => setEditing(false)}
      />
    );
  }

  const mkUrls: MarketplaceUrl[] = Array.isArray(product.marketplaceUrls) ? product.marketplaceUrls : [];
  const cutappLink = cutappLinkOf(product);

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-start gap-3">
          {onToggleSelect && (
            <input
              type="checkbox"
              checked={!!selected}
              onChange={() => onToggleSelect(product.id)}
              className="mt-1 h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            />
          )}
          <div>
            <p className="font-bold text-slate-800 text-base">{product.name}</p>
            <div className="flex flex-wrap gap-2 mt-1">
              {product.sku && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">SKU: {product.sku}</span>}
              {product.category && <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] text-slate-500">{product.category}</span>}
              {product.price && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">{product.price}</span>}
            </div>
          </div>
        </div>
        <div className="flex gap-2 shrink-0">
          <button onClick={() => setEditing(true)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-50">Editar</button>
          <button onClick={handleDelete} className="rounded-lg border border-red-200 px-3 py-1.5 text-xs text-red-500 hover:bg-red-50">Apagar</button>
        </div>
      </div>

      {/* Fotos */}
      <PhotoGalleryEditor
        productId={product.id}
        imageUrls={product.imageUrls}
        onUpdated={onUpdated}
      />

      {/* Vídeos verticais salvos no produto (gerados em Vídeos E-commerce) */}
      <ProductVideoGallery productId={product.id} />

      {/* Copy fields */}
      {(product.description || product.benefit || product.pain || product.dream || product.objection) && (
        <div className="grid gap-2 sm:grid-cols-2 text-xs text-slate-600 border-t border-slate-100 pt-3">
          {product.description && <div><span className="font-semibold text-slate-400">Descrição: </span>{product.description}</div>}
          {product.benefit && <div><span className="font-semibold text-slate-400">Benefício: </span>{product.benefit}</div>}
          {product.pain && <div><span className="font-semibold text-slate-400">Dor: </span>{product.pain}</div>}
          {product.dream && <div><span className="font-semibold text-slate-400">Sonho: </span>{product.dream}</div>}
          {product.objection && <div><span className="font-semibold text-slate-400">Objeção: </span>{product.objection}</div>}
        </div>
      )}

      {/* Links */}
      {(product.url || mkUrls.length > 0 || cutappLink) && (
        <div className="border-t border-slate-100 pt-3 space-y-1">
          {product.url && (
            <a href={product.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-xs text-indigo-500 hover:underline">
              <span>Site do produto</span>
            </a>
          )}
          {mkUrls.map((mk, i) => (
            <a key={i} href={mk.url} target="_blank" rel="noopener noreferrer" className="flex flex-wrap items-center gap-2 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2 text-xs text-slate-600 hover:border-indigo-200 hover:bg-indigo-50">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-slate-700 shadow-sm">
                <PlatformIcon platform={platformKey(mk.platform)} size={16} />
              </span>
              <span className="font-semibold text-slate-700">{mk.platform}</span>
              {mk.price && <span className="font-mono text-emerald-700">{mk.price}</span>}
              {mk.listingId && <span className="font-mono text-slate-400">ID: {mk.listingId}</span>}
            </a>
          ))}
          {/* Cut App: vinculo de ERP, nao de anuncio — nao tem URL publica, entao e
              um chip estatico ao lado dos marketplaces. Enquanto ele aparece aqui, o
              produto NAO e apagado pela poda dos marketplaces. */}
          {cutappLink && (
            <div className="flex flex-wrap items-center gap-2 rounded-xl border border-purple-100 bg-purple-50/60 px-3 py-2 text-xs text-slate-600">
              <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-slate-700 shadow-sm">
                <PlatformIcon platform="cutapp" size={16} />
              </span>
              <span className="font-semibold text-purple-700">Cut App</span>
              {cutappLink.sourceCatalogName && <span className="text-slate-500">{cutappLink.sourceCatalogName}</span>}
              {cutappLink.sku && <span className="font-mono text-slate-400">SKU: {cutappLink.sku}</span>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Products Tab ─────────────────────────────────────────────────────────────

function ProductSyncConfigModal({ label, initial, onClose, onSave }: { label: string; initial?: any; onClose: () => void; onSave: (fields: any) => void }) {
  const [fields, setFields] = useState({ price: initial?.price ?? true, name: initial?.name ?? false, description: initial?.description ?? false, photos: initial?.photos ?? false, videos: initial?.videos ?? false });
  const toggle = (key: string) => setFields(prev => ({ ...prev, [key]: !(prev as any)[key] }));
  const option = (key: string, title: string, desc: string) => (
    <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 hover:bg-white">
      <input type="checkbox" checked={(fields as any)[key]} onChange={() => toggle(key)} className="mt-0.5 accent-indigo-600" />
      <span><span className="block text-sm font-semibold text-slate-800">{title}</span><span className="block text-xs text-slate-500">{desc}</span></span>
    </label>
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg max-h-[90dvh] overflow-y-auto rounded-2xl bg-white p-5 shadow-2xl">
        <p className="text-xs font-bold uppercase tracking-widest text-indigo-600">Sincronizacao automatica</p>
        <h3 className="text-lg font-black text-slate-900">{label}</h3>
        <p className="mb-4 mt-1 text-xs text-slate-500">Quando habilitada, esta conexao sera sincronizada automaticamente a cada 24h por tenant.</p>
        <div className="space-y-2">
          {option('price', 'Preco', 'Atualiza o preco do produto e do vinculo do anuncio.')}
          {option('name', 'Nome', 'Atualiza o nome do produto usando o titulo do marketplace.')}
          {option('description', 'Descricao', 'Atualiza a descricao quando o marketplace retornar esse campo.')}
          {option('photos', 'Fotos', 'Substitui as fotos locais pelas fotos atuais do anuncio.')}
          {option('videos', 'Vídeos', 'Importa o vídeo do anúncio e salva no produto, junto com as fotos (salva o link público).')}
          {/* Mesmo aviso do Cut App: o sync de marketplace tambem substitui o array
              inteiro de imageUrls, descartando as fotos dos pipelines. */}
          {fields.photos && <PhotoOverwriteWarning compact sourceLabel="do anuncio" />}
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button onClick={onClose} className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Cancelar</button>
          <button onClick={() => onSave(fields)} className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700">Habilitar auto 24h</button>
        </div>
      </div>
    </div>
  );
}

const DEFAULT_PRODUCT_SYNC_FIELDS = { price: true, name: false, description: false, photos: false, videos: false };

function ProductSyncFieldsEditor({ fields, onChange }: { fields: any; onChange: (fields: any) => void }) {
  const setFields = (updater: any) => onChange(typeof updater === 'function' ? updater(fields) : updater);
  const toggle = (key: string) => setFields((prev: any) => ({ ...prev, [key]: !prev[key] }));
  const option = (key: string, title: string, desc: string) => (
    <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 hover:bg-white">
      <input type="checkbox" checked={(fields as any)[key]} onChange={() => toggle(key)} className="mt-0.5 accent-indigo-600" />
      <span><span className="block text-sm font-semibold text-slate-800">{title}</span><span className="block text-xs text-slate-500">{desc}</span></span>
    </label>
  );
  return (
    <div className="space-y-2">
      {option('price', 'Preco', 'Atualiza o preco do produto e do vinculo do anuncio.')}
      {option('name', 'Titulo/Nome', 'Persiste o titulo do anuncio no produto do Business Core.')}
      {option('description', 'Descricao', 'Persiste a descricao do anuncio no produto do Business Core.')}
      {option('photos', 'Fotos', 'Substitui as fotos locais pelas fotos atuais do anuncio.')}
      {option('videos', 'Videos', 'Persiste links de videos do anuncio no produto do Business Core.')}
      {fields?.photos && <PhotoOverwriteWarning compact sourceLabel="do anuncio" />}
    </div>
  );
}

function MarketplaceSyncSettingsModal({ shopeeConnections, mercadoLivreConnections, syncingConnectionId, onClose, onSyncShopee, onSyncMercadoLivre, onSaveConfig, onDisableConfig }: { shopeeConnections: any[]; mercadoLivreConnections: any[]; syncingConnectionId: string | null; onClose: () => void; onSyncShopee: (connection: any) => void; onSyncMercadoLivre: (connection: any) => void; onSaveConfig: (marketplace: string, connection: any, fields: any) => void; onDisableConfig: (marketplace: string, connection: any) => void }) {
  const allConnections = [
    ...shopeeConnections.map(connection => ({ marketplace: 'shopee', platform: 'shopee', connection, label: connection.displayName || connection.connectionName || connection.shopName || `Shopee ${connection.shopId}` })),
    ...mercadoLivreConnections.map(connection => ({ marketplace: 'mercado-livre', platform: 'mercadolivre', connection, label: connection.displayName || connection.connectionName || connection.nickname || `Mercado Livre ${connection.userId}` })),
  ];
  const [expanded, setExpanded] = useState<string | null>(allConnections[0]?.connection.id || null);
  const [drafts, setDrafts] = useState<Record<string, any>>(() => Object.fromEntries(allConnections.map(item => [item.connection.id, { ...DEFAULT_PRODUCT_SYNC_FIELDS, ...(item.connection.productSyncConfig?.fields || {}) }])));
  const setDraft = (connectionId: string, fields: any) => setDrafts(prev => ({ ...prev, [connectionId]: fields }));

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[86vh] w-full max-w-3xl overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="border-b border-slate-100 p-5">
          <p className="text-xs font-bold uppercase tracking-widest text-indigo-600">Configuracoes de sincronizacao</p>
          <h3 className="text-lg font-black text-slate-900">Sincronizacao com Marketplaces</h3>
          <p className="mt-1 text-xs text-slate-500">Os anuncios sempre guardam os dados por canal. Marque quais conexoes podem persistir preco, titulo, descricao, fotos e videos nos produtos do Business Core. Se o mesmo SKU existir em mais de uma conexao habilitada, a ultima sincronizacao pode sobrescrever esses campos.</p>
        </div>
        <div className="max-h-[62vh] overflow-y-auto p-4">
          {allConnections.length === 0 ? (
            <p className="rounded-xl border border-dashed border-slate-200 p-6 text-center text-sm text-slate-400">Nenhuma conexao ativa encontrada.</p>
          ) : allConnections.map(item => {
            const isExpanded = expanded === item.connection.id;
            const enabled = !!item.connection.productSyncConfig?.enabled;
            const syncing = syncingConnectionId === item.connection.id;
            return (
              <div key={item.connection.id} className="mb-2 overflow-hidden rounded-xl border border-slate-200 bg-white">
                <div className="flex flex-wrap items-center gap-2 bg-slate-50 px-3 py-2">
                  <button onClick={() => setExpanded(isExpanded ? null : item.connection.id)} className="flex min-w-0 flex-1 items-center gap-2 text-left">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white shadow-sm"><PlatformIcon platform={item.platform} size={18} /></span>
                    <span className="min-w-0"><span className="block truncate text-sm font-bold text-slate-800">{item.label}</span><span className="block text-[10px] font-semibold uppercase tracking-wider text-slate-400">{enabled ? 'Auto ativo' : 'Auto desativado'}</span></span>
                  </button>
                  <button onClick={() => item.marketplace === 'shopee' ? onSyncShopee(item.connection) : onSyncMercadoLivre(item.connection)} disabled={!!syncingConnectionId} className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-50">{syncing ? 'Sincronizando...' : 'Sincronizar Agora'}</button>
                </div>
                {isExpanded && (
                  <div className="space-y-3 p-3">
                    <ProductSyncFieldsEditor fields={drafts[item.connection.id] || DEFAULT_PRODUCT_SYNC_FIELDS} onChange={fields => setDraft(item.connection.id, fields)} />
                    <div className="flex justify-end gap-2 border-t border-slate-100 pt-3">
                      {enabled && <button onClick={() => onDisableConfig(item.marketplace, item.connection)} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">Desativar auto</button>}
                      <button onClick={() => onSaveConfig(item.marketplace, item.connection, drafts[item.connection.id] || DEFAULT_PRODUCT_SYNC_FIELDS)} className="rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700">Salvar e ativar auto 24h</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div className="flex justify-end border-t border-slate-100 p-4">
          <button onClick={onClose} className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Fechar</button>
        </div>
      </div>
    </div>
  );
}

function ProductsTab() {
  const [products, setProducts] = useState<Product[]>([]);
  const [productSearch, setProductSearch] = useState('');
  const [shopeeConnections, setShopeeConnections] = useState<any[]>([]);
  const [mercadoLivreConnections, setMercadoLivreConnections] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [syncingConnectionId, setSyncingConnectionId] = useState<string | null>(null);
  const [syncConfigModal, setSyncConfigModal] = useState<any | null>(null);
  const [showSyncSettingsModal, setShowSyncSettingsModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);
  // Cut App: so aparece para quem tem conexao. Ausencia de conexao e o caso normal
  // da maioria dos tenants - sem erro, sem toast, sem log no console.
  const [cutappConnection, setCutappConnection] = useState<CutappConnection | null>(null);
  const [cutappImporting, setCutappImporting] = useState(false);
  const [cutappModalOpen, setCutappModalOpen] = useState(false);
  const [cutappSaving, setCutappSaving] = useState(false);
  // Confirmacao de sobrescrita de fotos no sync manual de marketplace.
  const [photoSyncConfirm, setPhotoSyncConfirm] = useState<{ marketplace: string; connection: any; label: string } | null>(null);

  useEffect(() => {
    api.listProducts()
      .then(res => { if (res.success) setProducts(res.products); })
      .catch(() => toast.error('Erro ao carregar produtos.'))
      .finally(() => setLoading(false));

    api.listShopeeConnections()
      .then(res => setShopeeConnections((res.data || []).filter((connection: any) => connection.status === 'active')))
      .catch(() => setShopeeConnections([]));
    api.listMercadoLivreConnections()
      .then(res => setMercadoLivreConnections((res.data || []).filter((connection: any) => connection.status === 'active')))
      .catch(() => setMercadoLivreConnections([]));

    // Uma unica consulta ao estado da conexao Cut App.
    api.getCutappConnection()
      .then(res => setCutappConnection(unwrapCutappConnection(res)))
      .catch(() => setCutappConnection(null));
  }, []);

  const handleCreated = (p: Product) => { setProducts(prev => [p, ...prev]); setShowForm(false); };
  const handleUpdated = (p: Product) => setProducts(prev => prev.map(x => x.id === p.id ? p : x));
  const handleDeleted = (id: string) => { setProducts(prev => prev.filter(x => x.id !== id)); setSelectedIds(prev => { const s = new Set(prev); s.delete(id); return s; }); };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredProducts.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredProducts.map(p => p.id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Apagar ${selectedIds.size} produto(s)?\n\nTodos os registros de métricas vinculados também serão apagados. Essa ação não pode ser desfeita.`)) return;
    setBulkDeleting(true);
    let deleted = 0;
    for (const id of selectedIds) {
      try {
        await api.deleteProduct(id);
        deleted++;
      } catch {}
    }
    setProducts(prev => prev.filter(x => !selectedIds.has(x.id)));
    setSelectedIds(new Set());
    setBulkDeleting(false);
    toast.success(`${deleted} produto(s) apagado(s).`);
  };
  const normalizedSearch = productSearch.trim().toLowerCase();
  const filteredProducts = normalizedSearch
    ? products.filter(product => `${product.name || ''} ${product.sku || ''}`.toLowerCase().includes(normalizedSearch))
    : products;

  // O sync manual passou a mandar o `productSyncConfig.fields` salvo pelo usuario.
  // Antes ia sem opcoes e o backend aplicava o default dele (`photos !== false`),
  // ou seja: o clique no chip sobrescrevia as fotos mesmo com "Fotos" desmarcado na
  // config de auto 24h. Quando nao ha config salva, continuamos sem enviar opcoes
  // (nada muda no comportamento) mas pedimos confirmacao antes, porque nesse caso o
  // backend vai sobrescrever as fotos.
  const runMarketplaceSync = async (marketplace: string, connection: any, fields?: any) => {
    setSyncingConnectionId(connection.id);
    try {
      const res = marketplace === 'shopee'
        ? await api.syncShopeeProducts(connection.id, fields)
        : await api.syncMercadoLivreProducts(connection.id, fields);
      if (res.success) {
        setProducts(res.products || []);
        const nome = marketplace === 'shopee' ? 'Shopee sincronizada' : 'Mercado Livre sincronizado';
        // Poda: anúncios que o marketplace confirmou como excluídos perdem o vínculo e,
        // se o produto ficou órfão e veio deste sync, ele é removido da base.
        const listaVinculos = Array.isArray(res.vinculosRemovidos) ? res.vinculosRemovidos : [];
        const listaProdutos = Array.isArray(res.produtosRemovidos) ? res.produtosRemovidos : [];
        const extra = listaVinculos.length || listaProdutos.length
          ? ` ${listaVinculos.length} vínculo(s) removido(s), ${listaProdutos.length} produto(s) excluído(s).`
          : '';
        toast.success(`${nome}: ${res.created} criado(s), ${res.updated} atualizado(s), ${res.skipped} sem SKU.${extra}`);

        // Exclusão de produto é destrutiva: além da contagem, mostra QUAIS foram, num
        // toast próprio e mais demorado. Contagem sozinha não deixa o usuário conferir.
        if (listaProdutos.length > 0) {
          const nomes = listaProdutos
            .slice(0, 5)
            .map((item: any) => item?.sku || item?.nome || item?.productId)
            .filter(Boolean);
          const resto = listaProdutos.length - nomes.length;
          toast(
            `${listaProdutos.length} produto(s) excluído(s) da base porque o anúncio não existe mais no marketplace:\n`
            + `${nomes.join(', ')}${resto > 0 ? ` e mais ${resto}` : ''}`,
            { icon: '🗑️', duration: 12000, style: { maxWidth: '520px', whiteSpace: 'pre-line' } }
          );
        }
        // Vínculo removido sem apagar produto: o produto continua, só perdeu aquele anúncio.
        if (listaVinculos.length > 0 && listaProdutos.length === 0) {
          toast(`${listaVinculos.length} vínculo(s) de anúncio removido(s). Os produtos foram mantidos.`, {
            icon: 'ℹ️', duration: 8000,
          });
        }
      }
    } catch (err: any) {
      toast.error(err.message || (marketplace === 'shopee' ? 'Erro ao sincronizar Shopee.' : 'Erro ao sincronizar Mercado Livre.'));
    } finally {
      setSyncingConnectionId(null);
    }
  };

  const requestMarketplaceSync = (marketplace: string, connection: any, label: string) => {
    const savedFields = connection.productSyncConfig?.fields;
    const willOverwritePhotos = savedFields ? !!savedFields.photos : true;
    if (willOverwritePhotos) {
      setPhotoSyncConfirm({ marketplace, connection, label });
      return;
    }
    runMarketplaceSync(marketplace, connection, savedFields);
  };

  const handleSyncShopee = (connection: any) => requestMarketplaceSync(
    'shopee',
    connection,
    connection.displayName || connection.connectionName || connection.shopName || `Shopee ${connection.shopId}`,
  );

  const handleSyncMercadoLivre = (connection: any) => requestMarketplaceSync(
    'mercado-livre',
    connection,
    connection.displayName || connection.connectionName || connection.nickname || `Mercado Livre ${connection.userId}`,
  );

  // ─── Cut App ────────────────────────────────────────────────────────────────
  const handleImportCutapp = async () => {
    const catalogIds = (cutappConnection?.catalogIds || []).map(String);
    if (catalogIds.length === 0) {
      toast.error('Selecione ao menos um catalogo em Conexoes > ERPs > Cut App.');
      return;
    }
    setCutappImporting(true);
    try {
      const report = await runCutappProductImport(catalogIds);
      toast.success(cutappReportSummary(report), { duration: report?.priceSync ? 10000 : 4000 });
      const res = await api.listProducts();
      if (res.success) setProducts(res.products);
      setCutappConnection(prev => (prev ? { ...prev, lastSyncAt: new Date().toISOString() } : prev));
    } catch (err: any) {
      toast.error(err.message || 'Erro ao importar produtos do Cut App.');
    } finally {
      setCutappImporting(false);
    }
  };

  const saveCutappAutoSync = async (fields: Required<CutappFieldOptions>, photoMode: CutappPhotoMode) => {
    setCutappSaving(true);
    try {
      await api.updateCutappConnection({ fieldOptions: fields, photoMode, syncEnabled: true });
      setCutappConnection(prev => (prev ? { ...prev, fieldOptions: fields, photoMode, syncEnabled: true } : prev));
      toast.success('Sincronizacao automatica do Cut App habilitada. Ela roda 1x por dia.');
      setCutappModalOpen(false);
    } catch (err: any) {
      toast.error(err.message || 'Erro ao salvar sincronizacao automatica do Cut App.');
    } finally {
      setCutappSaving(false);
    }
  };

  const disableCutappAutoSync = async () => {
    try {
      await api.updateCutappConnection({ syncEnabled: false });
      setCutappConnection(prev => (prev ? { ...prev, syncEnabled: false } : prev));
      toast.success('Sincronizacao automatica do Cut App desabilitada.');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao desabilitar sincronizacao do Cut App.');
    }
  };

  const saveAutoSyncConfig = async (fields: any) => {
    if (!syncConfigModal) return;
    const { marketplace, connection } = syncConfigModal;
    const config = { enabled: true, intervalHours: 24, fields };
    try {
      const res = await api.updateMarketplaceProductSyncConfig(marketplace, connection.id, config);
      const update = (item: any) => item.id === connection.id ? { ...item, productSyncConfig: config, productLastSyncAt: res.connection?.productLastSyncAt || item.productLastSyncAt } : item;
      if (marketplace === 'shopee') setShopeeConnections(prev => prev.map(update));
      if (marketplace === 'mercado-livre') setMercadoLivreConnections(prev => prev.map(update));
      toast.success('Sincronizacao automatica habilitada. Ela roda a cada 24h por tenant.');
      setSyncConfigModal(null);
    } catch (err: any) {
      toast.error(err.message || 'Erro ao salvar sincronizacao automatica.');
    }
  };

  const saveConnectionSyncConfig = async (marketplace: string, connection: any, fields: any) => {
    const config = { enabled: true, intervalHours: 24, fields };
    try {
      const res = await api.updateMarketplaceProductSyncConfig(marketplace, connection.id, config);
      const update = (item: any) => item.id === connection.id ? { ...item, productSyncConfig: config, productLastSyncAt: res.connection?.productLastSyncAt || item.productLastSyncAt } : item;
      if (marketplace === 'shopee') setShopeeConnections(prev => prev.map(update));
      if (marketplace === 'mercado-livre') setMercadoLivreConnections(prev => prev.map(update));
      toast.success('Configuracao de sincronizacao salva. Auto 24h ativo para esta conexao.');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao salvar sincronizacao automatica.');
    }
  };

  const disableAutoSync = async (marketplace: string, connection: any) => {
    const config = { enabled: false, intervalHours: 24, fields: connection.productSyncConfig?.fields || { price: true, name: false, description: false, photos: false, videos: false } };
    try {
      await api.updateMarketplaceProductSyncConfig(marketplace, connection.id, config);
      const update = (item: any) => item.id === connection.id ? { ...item, productSyncConfig: config } : item;
      if (marketplace === 'shopee') setShopeeConnections(prev => prev.map(update));
      if (marketplace === 'mercado-livre') setMercadoLivreConnections(prev => prev.map(update));
      toast.success('Sincronizacao automatica desabilitada.');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao desabilitar sincronizacao.');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            checked={filteredProducts.length > 0 && selectedIds.size === filteredProducts.length}
            onChange={toggleSelectAll}
            className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
            title="Selecionar todos"
          />
          <div>
            <p className="text-sm text-slate-500">{products.length} produto{products.length !== 1 ? 's' : ''} cadastrado{products.length !== 1 ? 's' : ''}</p>
            {normalizedSearch && <p className="text-xs text-slate-400">{filteredProducts.length} encontrado{filteredProducts.length !== 1 ? 's' : ''} para a busca</p>}
          </div>
          {selectedIds.size > 0 && (
            <button
              onClick={handleBulkDelete}
              disabled={bulkDeleting}
              className="rounded-lg bg-red-50 border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-600 hover:bg-red-100 transition disabled:opacity-50"
            >
              {bulkDeleting ? 'Apagando...' : `Apagar ${selectedIds.size} selecionado${selectedIds.size > 1 ? 's' : ''}`}
            </button>
          )}
        </div>
        <div className="flex w-full flex-col gap-2 sm:flex-row md:w-auto">
          <div className="flex min-w-0 items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm sm:w-80">
            <span className="text-sm text-slate-400">Buscar</span>
            <input
              value={productSearch}
              onChange={e => setProductSearch(e.target.value)}
              placeholder="SKU ou nome"
              className="min-w-0 flex-1 bg-transparent text-sm text-slate-700 outline-none placeholder:text-slate-300"
            />
            {productSearch && (
              <button onClick={() => setProductSearch('')} className="text-xs font-semibold text-slate-400 hover:text-slate-600">Limpar</button>
            )}
          </div>
          <button
            onClick={() => setShowForm(v => !v)}
            className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 transition-colors"
          >
            {showForm ? 'Cancelar' : '+ Novo produto'}
          </button>
        </div>
      </div>

      {(shopeeConnections.length > 0 || mercadoLivreConnections.length > 0) && (
        <button
          onClick={() => setShowSyncSettingsModal(true)}
          className="inline-flex items-center justify-center rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-100 transition-colors"
        >
          Configuracoes Sincronizacao com Marketplaces
        </button>
      )}

      {(shopeeConnections.length > 0 || mercadoLivreConnections.length > 0 || !!cutappConnection) && (
        <div className="rounded-2xl border border-orange-100 bg-orange-50/60 p-4">
          <p className="mb-3 text-xs font-bold uppercase tracking-widest text-orange-700">Sincronizacao de produtos</p>
          <div className="flex flex-wrap gap-2">
            {shopeeConnections.map(connection => {
              const label = connection.displayName || connection.connectionName || connection.shopName || `Shopee ${connection.shopId}`;
              const syncing = syncingConnectionId === connection.id;
              return (
                <div key={connection.id} className="flex items-center gap-2 rounded-xl border border-orange-200 bg-white px-2.5 py-1.5 shadow-sm">
                  <button
                    onClick={() => handleSyncShopee(connection)}
                    disabled={!!syncingConnectionId}
                    className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-orange-700 disabled:opacity-60"
                  >
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-orange-50">
                    <PlatformIcon platform="shopee" size={18} />
                  </span>
                  <span>{syncing ? 'Sincronizando...' : label}</span>
                  </button>
                  <label className="flex items-center gap-1 border-l border-slate-100 pl-2 text-[11px] text-slate-500">
                    <input type="checkbox" className="accent-orange-500" checked={!!connection.productSyncConfig?.enabled} onChange={e => e.target.checked ? setSyncConfigModal({ marketplace: 'shopee', connection, label }) : disableAutoSync('shopee', connection)} />
                    Auto 24h
                  </label>
                </div>
              );
            })}
            {mercadoLivreConnections.map(connection => {
              const label = connection.displayName || connection.connectionName || connection.nickname || `Mercado Livre ${connection.userId}`;
              const syncing = syncingConnectionId === connection.id;
              return (
                <div key={connection.id} className="flex items-center gap-2 rounded-xl border border-yellow-200 bg-white px-2.5 py-1.5 shadow-sm">
                  <button
                    onClick={() => handleSyncMercadoLivre(connection)}
                    disabled={!!syncingConnectionId}
                    className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-yellow-700 disabled:opacity-60"
                  >
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-yellow-50">
                    <PlatformIcon platform="mercadolivre" size={18} />
                  </span>
                  <span>{syncing ? 'Sincronizando...' : label}</span>
                  </button>
                  <label className="flex items-center gap-1 border-l border-slate-100 pl-2 text-[11px] text-slate-500">
                    <input type="checkbox" className="accent-yellow-500" checked={!!connection.productSyncConfig?.enabled} onChange={e => e.target.checked ? setSyncConfigModal({ marketplace: 'mercado-livre', connection, label }) : disableAutoSync('mercado-livre', connection)} />
                    Auto 24h
                  </label>
                </div>
              );
            })}
            {/* Cut App: mesma mecanica dos marketplaces. So renderiza se o tenant
                tiver conexao com o Cut App. */}
            {cutappConnection && (
              <div className="flex items-center gap-2 rounded-xl border border-purple-200 bg-white px-2.5 py-1.5 shadow-sm">
                <button
                  onClick={handleImportCutapp}
                  disabled={cutappImporting}
                  className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-purple-700 disabled:opacity-60"
                >
                  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-purple-50">
                    <img src="/cutapp.svg" alt="" className="h-4 w-4 object-contain" />
                  </span>
                  <span>{cutappImporting ? 'Importando...' : 'Cut App'}</span>
                </button>
                <label className="flex items-center gap-1 border-l border-slate-100 pl-2 text-[11px] text-slate-500">
                  <input
                    type="checkbox"
                    className="accent-purple-500"
                    checked={!!cutappConnection.syncEnabled}
                    onChange={e => e.target.checked ? setCutappModalOpen(true) : disableCutappAutoSync()}
                  />
                  Auto 24h
                </label>
              </div>
            )}
          </div>
        </div>
      )}

      {cutappModalOpen && (
        <CutappProductSyncModal
          initialFields={cutappConnection?.fieldOptions || DEFAULT_CUTAPP_FIELD_OPTIONS}
          initialPhotoMode={cutappConnection?.photoMode}
          saving={cutappSaving}
          onClose={() => setCutappModalOpen(false)}
          onSave={saveCutappAutoSync}
        />
      )}

      {photoSyncConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="border-b border-slate-200 px-5 py-4">
              <h2 className="text-base font-bold text-red-700">Esta sincronizacao vai sobrescrever as fotos</h2>
              <p className="text-sm text-slate-500">{photoSyncConfirm.label} - leia antes de continuar.</p>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-5 py-4">
              <PhotoOverwriteWarning sourceLabel="do anuncio" />
              {!photoSyncConfirm.connection.productSyncConfig?.fields && (
                <p className="mt-3 text-xs text-slate-500">
                  Esta conexao ainda nao tem configuracao de campos salva, entao a sincronizacao usa o padrao do
                  servidor - que inclui as fotos. Use "Sincronizar sem tocar nas fotos" ou configure em Auto 24h.
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2 border-t border-slate-200 px-5 py-4">
              <button
                onClick={() => setPhotoSyncConfirm(null)}
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  const { marketplace, connection } = photoSyncConfirm;
                  setPhotoSyncConfirm(null);
                  runMarketplaceSync(marketplace, connection, { ...DEFAULT_PRODUCT_SYNC_FIELDS, ...(connection.productSyncConfig?.fields || {}), photos: false });
                }}
                className="rounded-xl border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 hover:bg-emerald-100"
              >
                Sincronizar sem tocar nas fotos
              </button>
              <button
                onClick={() => {
                  const { marketplace, connection } = photoSyncConfirm;
                  setPhotoSyncConfirm(null);
                  runMarketplaceSync(marketplace, connection, connection.productSyncConfig?.fields);
                }}
                className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700"
              >
                Entendi, sobrescrever as fotos
              </button>
            </div>
          </div>
        </div>
      )}

      {syncConfigModal && (
        <ProductSyncConfigModal
          label={syncConfigModal.label}
          initial={syncConfigModal.connection.productSyncConfig?.fields}
          onClose={() => setSyncConfigModal(null)}
          onSave={saveAutoSyncConfig}
        />
      )}

      {showSyncSettingsModal && (
        <MarketplaceSyncSettingsModal
          shopeeConnections={shopeeConnections}
          mercadoLivreConnections={mercadoLivreConnections}
          syncingConnectionId={syncingConnectionId}
          onClose={() => setShowSyncSettingsModal(false)}
          onSyncShopee={handleSyncShopee}
          onSyncMercadoLivre={handleSyncMercadoLivre}
          onSaveConfig={saveConnectionSyncConfig}
          onDisableConfig={disableAutoSync}
        />
      )}

      {showForm && (
        <ProductForm onSave={handleCreated} onCancel={() => setShowForm(false)} />
      )}

      {loading ? (
        <p className="text-sm text-slate-400 animate-pulse">Carregando produtos...</p>
      ) : products.length === 0 && !showForm ? (
        <div className="rounded-2xl border border-dashed border-slate-200 p-10 text-center">
          <p className="text-slate-400 font-medium">Nenhum produto cadastrado</p>
          <p className="text-slate-500 text-sm mt-1">Adicione produtos para usar como contexto nos briefs e pipelines de marketing.</p>
        </div>
      ) : filteredProducts.length === 0 && !showForm ? (
        <div className="rounded-2xl border border-dashed border-slate-200 p-10 text-center">
          <p className="text-slate-400 font-medium">Nenhum produto encontrado</p>
          <p className="text-slate-500 text-sm mt-1">Tente buscar por outro SKU ou nome.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredProducts.map(p => (
            <ProductCard key={p.id} product={p} onUpdated={handleUpdated} onDeleted={handleDeleted} selected={selectedIds.has(p.id)} onToggleSelect={toggleSelect} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

type Tab = 'produtos' | 'conhecimento' | 'conexoes' | 'mcp' | 'creditos' | 'usuarios';
type ConnectionsTab = 'whatsapp' | 'marketplaces' | 'erps' | 'redes-sociais';

function ConnectionsTabContent() {
  const [connectionTab, setConnectionTab] = useState<ConnectionsTab>('whatsapp');

  const tabBtn = (t: ConnectionsTab, label: string) => {
    const active = connectionTab === t;
    return (
      <button
        onClick={() => setConnectionTab(t)}
        className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold transition-all ${
          active ? 'bg-white shadow-sm text-emerald-700' : 'text-slate-500 hover:text-slate-700'
        }`}
      >
        {label}
      </button>
    );
  };

  return (
    <div className="space-y-4">
      <div className="inline-flex flex-wrap gap-1 rounded-2xl bg-slate-100 p-1">
        {tabBtn('whatsapp', 'Whatsapp Não Oficial')}
        {tabBtn('marketplaces', 'Marketplaces')}
        {tabBtn('erps', 'ERPs')}
        {tabBtn('redes-sociais', 'Redes Sociais Oficiais')}
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm [&_.main-content]:ml-0 [&_.main-content]:w-full [&_.main-content]:p-0">
        {connectionTab === 'whatsapp' && <WhatsAppConnectionsPage />}
        {connectionTab === 'marketplaces' && <MarketplacesPage />}
        {connectionTab === 'erps' && <ErpsPage />}
        {connectionTab === 'redes-sociais' && <SocialConnectionsPage />}
      </div>
    </div>
  );
}

export function BusinessCorePage() {
  const { canSee } = useMenuPermissions();
  const { user } = useAuth();
  const isAdmin = ['ADMIN', 'TENANT_ADMIN', 'SUPERADMIN'].includes(user?.role || '');
  const allowedTabs = (['produtos', 'conhecimento', 'conexoes', 'mcp', 'creditos', ...(isAdmin ? ['usuarios' as Tab] : [])] as Tab[])
    .filter(t => t === 'usuarios' ? canSee('business-core.usuarios') : canSee(`business-core.${t}`));
  // ?aba=mcp abre direto numa aba (link do Seller > Agente para o MCP).
  const [params] = useSearchParams();
  const [tab, setTab] = useState<Tab>(() => (params.get('aba') as Tab) || 'produtos');
  const activeTab: Tab = allowedTabs.includes(tab) ? tab : (allowedTabs[0] || 'produtos');
  const tabBtn = (t: Tab, label: string) => allowedTabs.includes(t) && (
    <button
      onClick={() => setTab(t)}
      className={`flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold transition-all ${
        activeTab === t ? 'bg-white shadow-md text-slate-800' : 'text-slate-500 hover:text-slate-700'
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-emerald-50/30">
      {/* Top bar */}
      <div className="sticky top-11 z-40 border-b border-slate-200 bg-white/90 backdrop-blur px-6 py-4 flex items-center justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600">Business Core</p>
          <h1 className="text-lg font-black text-slate-900">Central de informações da empresa</h1>
        </div>
      </div>

      {/* Sub-menu */}
      <div className="px-4 sm:px-6 pt-5 pb-2 overflow-x-auto">
        <div className="inline-flex gap-1 rounded-2xl bg-slate-100 p-1 whitespace-nowrap">
          {tabBtn('produtos', 'Produtos')}
          {tabBtn('conhecimento', 'Base de Conhecimento')}
          {tabBtn('conexoes', 'Conexões')}
          {tabBtn('mcp', 'MCP')}
          {tabBtn('creditos', 'Créditos')}
          {tabBtn('usuarios', 'Usuários')}
        </div>
      </div>

      {/* Content */}
      <div className={`px-6 py-4 ${activeTab === 'conexoes' || activeTab === 'usuarios' || activeTab === 'mcp' ? 'max-w-7xl' : 'max-w-4xl'}`}>
        {activeTab === 'produtos' && allowedTabs.includes('produtos') && <ProductsTab />}
        {activeTab === 'conhecimento' && allowedTabs.includes('conhecimento') && <KnowledgeBasePage embedded />}
        {activeTab === 'conexoes' && allowedTabs.includes('conexoes') && <ConnectionsTabContent />}
        {activeTab === 'mcp' && allowedTabs.includes('mcp') && <McpConnectionsView />}
        {activeTab === 'creditos' && allowedTabs.includes('creditos') && <CreditsHistoryView />}
        {activeTab === 'usuarios' && allowedTabs.includes('usuarios') && <UsersPage />}
      </div>
    </div>
  );
}





